import { useState, useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navLinks = [
  { path: "/", label: "Accueil" },
  { path: "/contrats-entreprises", label: "Contrats Entreprises" },
  { path: "/blog", label: "Blog" },
  { path: "/a-propos", label: "A Propos" },
  { path: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [services, setServices] = useState([]);
  const dropRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/services`).then(r => setServices(r.data)).catch(() => {});
  }, []);

  const b2c = services.filter(s => s.type_client === "B2C" || s.type_client === "mixte");
  const b2b = services.filter(s => s.type_client === "B2B");

  const openDrop = () => { clearTimeout(timerRef.current); setDropOpen(true); };
  const closeDrop = () => { timerRef.current = setTimeout(() => setDropOpen(false), 200); };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#062133]/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2">
          <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
            <img src="/logo-black.png" alt="Nettoyage Expert - le spécialiste des textiles" className="h-[80px] sm:h-[100px] w-auto" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            <NavLink to="/" end className={({isActive}) => `text-sm font-medium transition-colors ${isActive ? "text-[#336DBF]" : "text-[#062133]/60 hover:text-[#062133]"}`}>Accueil</NavLink>

            {/* Services Dropdown */}
            <div className="relative" ref={dropRef} onMouseEnter={openDrop} onMouseLeave={closeDrop}>
              <NavLink to="/services" className={({isActive}) => `text-sm font-medium transition-colors flex items-center gap-1 ${isActive ? "text-[#336DBF]" : "text-[#062133]/60 hover:text-[#062133]"}`} data-testid="nav-services-trigger">
                Services <ChevronDown size={14} className={`transition-transform ${dropOpen ? "rotate-180" : ""}`} />
              </NavLink>

              {dropOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2" onMouseEnter={openDrop} onMouseLeave={closeDrop}>
                  <div className="bg-white border border-[#062133]/10 rounded-lg shadow-xl w-[520px] p-6" data-testid="services-dropdown">
                    <div className="grid grid-cols-2 gap-6">
                      {/* B2C Column */}
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#336DBF] mb-3">Particuliers</p>
                        <div className="space-y-1">
                          {b2c.map(s => (
                            <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setDropOpen(false)}
                              className="block px-2 py-1.5 rounded text-sm text-[#062133]/80 hover:bg-[#336DBF]/5 hover:text-[#336DBF] transition-colors" data-testid={`dropdown-${s.slug}`}>
                              {s.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                      {/* B2B Column */}
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#336DBF] mb-3">Entreprises</p>
                        <div className="space-y-1">
                          {b2b.map(s => (
                            <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setDropOpen(false)}
                              className="block px-2 py-1.5 rounded text-sm text-[#062133]/80 hover:bg-[#336DBF]/5 hover:text-[#336DBF] transition-colors" data-testid={`dropdown-${s.slug}`}>
                              {s.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-[#062133]/5">
                      <Link to="/services" onClick={() => setDropOpen(false)}
                        className="text-sm font-medium text-[#336DBF] hover:underline flex items-center gap-1">
                        Voir tous les services <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) => (
              <NavLink key={link.path} to={link.path}
                className={({isActive}) => `text-sm font-medium transition-colors ${isActive ? "text-[#336DBF]" : "text-[#062133]/60 hover:text-[#062133]"}`}>
                {link.label}
              </NavLink>
            ))}
            <Link to="/contact" className="inline-flex items-center justify-center px-5 py-2.5 bg-[#336DBF] text-white font-semibold text-sm rounded-md hover:bg-[#2A5A9E] transition-colors" data-testid="nav-cta">
              Devis Gratuit
            </Link>
          </nav>

          <button className="lg:hidden p-2 -mr-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={24} className="text-[#062133]" /> : <Menu size={24} className="text-[#062133]" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-[#062133]/10 absolute left-0 right-0 top-full shadow-lg z-50 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <nav className="flex flex-col px-4 py-4 space-y-1">
            <NavLink to="/" end onClick={() => setOpen(false)}
              className={({isActive}) => `block px-4 py-3 rounded-md text-sm font-medium ${isActive ? "bg-[#336DBF]/10 text-[#336DBF]" : "text-[#062133]/60 hover:bg-[#F4F6F8]"}`}>
              Accueil
            </NavLink>

            {/* Mobile Services Accordion */}
            <div>
              <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-md text-sm font-medium text-[#062133]/60 hover:bg-[#F4F6F8]"
                data-testid="mobile-services-toggle">
                Services <ChevronDown size={16} className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileServicesOpen && (
                <div className="ml-4 border-l-2 border-[#336DBF]/20 pl-2 space-y-0.5 pb-2" data-testid="mobile-services-list">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#336DBF] px-3 py-1.5">Particuliers</p>
                  {b2c.map(s => (
                    <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => { setOpen(false); setMobileServicesOpen(false); }}
                      className="block px-3 py-2 text-sm text-[#062133]/70 hover:text-[#336DBF] rounded">
                      {s.name}
                    </Link>
                  ))}
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#336DBF] px-3 py-1.5 mt-2">Entreprises</p>
                  {b2b.map(s => (
                    <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => { setOpen(false); setMobileServicesOpen(false); }}
                      className="block px-3 py-2 text-sm text-[#062133]/70 hover:text-[#336DBF] rounded">
                      {s.name}
                    </Link>
                  ))}
                  <Link to="/services" onClick={() => { setOpen(false); setMobileServicesOpen(false); }}
                    className="block px-3 py-2 text-sm font-medium text-[#336DBF]">
                    Voir tous les services &rarr;
                  </Link>
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) => (
              <NavLink key={link.path} to={link.path} onClick={() => setOpen(false)}
                className={({isActive}) => `block px-4 py-3 rounded-md text-sm font-medium ${isActive ? "bg-[#336DBF]/10 text-[#336DBF]" : "text-[#062133]/60 hover:bg-[#F4F6F8]"}`}>
                {link.label}
              </NavLink>
            ))}
            <Link to="/contact" onClick={() => setOpen(false)}
              className="block mx-4 mt-2 text-center px-5 py-3 bg-[#336DBF] text-white font-semibold text-sm rounded-md" data-testid="mobile-nav-cta">
              Devis Gratuit
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
